import React from "react";
import type { Policy } from "../../@lib/types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
  CardProps,
  Card as MUICard,
  CardMedia,
  CardContent,
  CardActionArea,
} from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import {
  baseURL,
  statusCode,
  status as statusKey,
} from "../../utils/constants";
import { StatusTag } from "../statusTag";

import { forwardRef } from "react";
import { useAuthSession } from "../AuthSessionProvider";
import { styled } from "@mui/material/styles";
import imagePlaceholder from "../../@lib/images/image-placeholder.svg";
import { ConditionalRenderer } from "../conditionalRenderer";
import { useLanguageTranslator } from "../useLanguageContext";
import dayjs from "dayjs";

export type CardPropsT = Omit<CardProps, "id"> &
  Pick<
    Policy,
    "id" | "title" | "description" | "status" | "statusLabel" | "picture"
  > & {
    disabled?: boolean;
    onDelete?: (id: string | number) => void;
    isPrivate?: boolean;
    path?: string;
  };

const CardSkeleton = (
  <div>
    <Skeleton variant="rounded" height={240} sx={{ mb: 1 }} />
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 1 }}
    >
      <Skeleton variant="rounded" width="30%" />
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
      >
        <Skeleton variant="text" width="50%" height={20} />
        <Skeleton variant="text" width="30%" height={16} />
      </Box>
    </Stack>
    <Skeleton variant="text" width="40%" height={20} />
    <Skeleton variant="text" width="80%" height={16} />

    <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
      <Skeleton variant="rounded" width="45%" height={24} />
      <Skeleton variant="rounded" width="45%" height={24} />
    </Stack>
  </div>
);

export { CardSkeleton };

const CardActionButton = styled(Button)(({ theme }) => ({
  textTransform: "uppercase",
  borderRadius: 100,
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  fontSize: 12,
  flex: 1,
  color: "#ffffff",
}));

const ForwardedCard: React.ForwardRefRenderFunction<HTMLDivElement, any> = (
  props,
  ref
) => {
  const {
    isPrivate = false,
    isAdmin = false,
    id,
    title,
    description,
    picture,
    status,
    statusLabel,
    createdAt,
    disabled = false,
    path = "",
    editPath = "",
    onDelete = () => {},
    onAdminActions = () => {},
    isAdminPeople,
    firstname,
    lastname,
    ...cardProps
  } = props;

  const { user } = useAuthSession();
  const { translator } = useLanguageTranslator();
  const isLoggedIn = !!user;

  return (
    <MUICard {...cardProps} ref={ref}>
      <CardActionArea component={RouterLink} to={path} disabled={disabled}>
        <CardMedia
          component="img"
          height={280}
          image={
            picture?.source ? `${baseURL}${picture?.source}` : imagePlaceholder
          }
          alt="policy papers"
        />
      </CardActionArea>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <ConditionalRenderer condition={isAdmin || isPrivate}>
            <StatusTag label={statusLabel} status={status} />
          </ConditionalRenderer>
          <div style={{ textAlign: "right" }}>
            <Typography variant="caption">
              {dayjs(createdAt?.split("T")?.[0] || "").format("DD.MM.YYYY")}
            </Typography>
          </div>
        </Stack>
        <Typography variant="h5" mt={1}>
          {isAdminPeople
            ? `${firstname} ${lastname}`
            : title?.length > 33
            ? `${title?.slice(0, 33)}...`
            : title}
        </Typography>
        <Typography component="p" variant="caption" noWrap>
          {description}
        </Typography>
        <ConditionalRenderer condition={isAdmin}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "10px 0px 0px 0px",
            }}
          >
            <div style={{ width: "110px", height: "36px" }}>
              <CardActionButton
                size="small"
                variant="contained"
                style={{
                  backgroundColor:
                    status === statusKey?.approved ? "#FF6D6D" : "#52D777",
                  width: "110px",
                  height: "36px",
                  borderRadius: "10px",
                }}
                onClick={() =>
                  onAdminActions &&
                  onAdminActions(
                    id,
                    status === statusKey?.approved
                      ? statusCode.rejected
                      : statusCode.approved
                  )
                }
              >
                {status === statusKey?.approved
                  ? translator("status.Reject")
                  : translator("status.Approve")}
              </CardActionButton>
            </div>
          </div>
        </ConditionalRenderer>
      </CardContent>
      <ConditionalRenderer
        condition={isPrivate && isLoggedIn && status !== "approved"}
      >
        <Stack direction="row" spacing={1} sx={{ p: 2, width: "100%" }}>
          <CardActionButton
            size="small"
            variant="contained"
            color="warning"
            disabled={disabled}
            href={editPath}
          >
            {translator("generic.EDIT")}
          </CardActionButton>
          <CardActionButton
            size="small"
            variant="contained"
            color="error"
            disabled={disabled}
            onClick={() => onDelete && onDelete(id)}
          >
            {translator("generic.DELETE")}
          </CardActionButton>
        </Stack>
      </ConditionalRenderer>
    </MUICard>
  );
};

const Card = forwardRef(ForwardedCard);

export { Card };
